import { useQuery } from "@tanstack/react-query";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { InvestorSidebar } from "@/components/dashboard/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AllocationChart } from "@/components/dashboard/allocation-chart";
import { DeploymentStatus } from "@/components/dashboard/deployment-status";
import { ThemeToggle } from "@/components/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Shield, PieChart, Clock } from "lucide-react";
import type { Portfolio } from "@shared/schema";
import { formatPlatformCurrency, formatPlatformCurrencyExact } from "@/lib/currency";

const formatCurrency = formatPlatformCurrency;
const formatCurrencyExact = formatPlatformCurrencyExact;

export default function PortfolioPage() {
  const { data: portfolio, isLoading } = useQuery<Portfolio>({
    queryKey: ["/api/investor/portfolio"],
  });
  const { data: returnsData, isLoading: loadingReturns } = useQuery<any[]>({
    queryKey: ["/api/investor/returns"],
  });
  
  // ... rest of component until return ...

  const sidebarStyle = {
    "--sidebar-width": "17rem",
    "--sidebar-width-icon": "3.5rem",
  };

  const privateCreditAlloc = parseFloat(portfolio?.privateCreditAllocation || "0");
  const aifAlloc = parseFloat(portfolio?.aifExposure || "0");
  const cashAlloc = parseFloat(portfolio?.cashEquivalents || "0");
  const currencyCommoditiesAlloc = parseFloat((portfolio as any)?.currencyCommodities || "0");

  const allocationData = [
    { name: "Private Credit", value: privateCreditAlloc, color: "hsl(var(--accent))" },
    { name: "AIF Exposure", value: aifAlloc, color: "hsl(var(--chart-2))" },
    { name: "Cash & Equivalents", value: cashAlloc, color: "hsl(var(--chart-3))" },
    { name: "Currency/Commodities", value: currencyCommoditiesAlloc, color: "hsl(280, 60%, 55%)" },
  ].filter(d => d.value > 0);

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full overflow-hidden">
        <InvestorSidebar />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <header className="flex items-center justify-between gap-4 h-16 px-6 border-b bg-background/95 backdrop-blur shrink-0">
            <div className="flex items-center gap-4">
              <SidebarTrigger data-testid="button-sidebar-toggle" />
              <div>
                <h1 className="text-lg font-semibold">Portfolio</h1>
                <p className="text-sm text-muted-foreground">View your allocation and holdings</p>
              </div>
            </div>
            <ThemeToggle />
          </header>

          <main className="flex-1 p-6 space-y-6 overflow-y-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Private Credit</p>
                      <p className="text-xl font-bold">{privateCreditAlloc}%</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Allocation</p>
                </CardContent>
              </Card>

              <Card className="bg-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-chart-2/10 flex items-center justify-center">
                      <PieChart className="w-5 h-5 text-chart-2" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">AIF Exposure</p>
                      <p className="text-xl font-bold">{aifAlloc}%</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Allocation</p>
                </CardContent>
              </Card>

              <Card className="bg-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-chart-3/10 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-chart-3" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Cash & Equivalents</p>
                      <p className="text-xl font-bold">{cashAlloc}%</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Allocation</p>
                </CardContent>
              </Card>

              <Card className="bg-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Currency/Commodities</p>
                      <p className="text-xl font-bold">{currencyCommoditiesAlloc}%</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Allocation</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <Card className="bg-card">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Total Invested</p>
                  <p className="text-2xl font-bold">{formatCurrency(portfolio?.totalInvested)}</p>
                </CardContent>
              </Card>
              <Card className="bg-card">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Current Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(portfolio?.currentValue)}</p>
                </CardContent>
              </Card>
              <Card className="bg-card">
                <CardContent className="p-6">
                  <p className="text-sm text-muted-foreground mb-1">Net Returns · IRR</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(portfolio?.returns)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{portfolio?.irr || 0}% Net IRR</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">My Deployments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {portfolio ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Product</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Investment Amount</th>
                              <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Allocation Date</th>
                              <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Maturity</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Returns (Till Date)</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                              <td className="py-4 px-4 font-semibold text-sm text-accent">
                                {portfolio.fundName || "Velocity Fund"}
                              </td>
                              <td className="py-4 px-4 text-right font-medium text-sm">
                                {formatCurrencyExact(portfolio.totalInvested)}
                              </td>
                              <td className="py-4 px-4 text-center text-sm font-medium">
                                {portfolio.allocationDate ? new Date(portfolio.allocationDate).toLocaleDateString("en-IN") : "—"}
                              </td>
                              <td className="py-4 px-4 text-center text-sm">
                                <Badge variant="secondary" className="font-normal text-xs">
                                  {portfolio.maturity || portfolio.lockIn || "36 Months"}
                                </Badge>
                              </td>
                              <td className="py-4 px-4 text-right font-bold text-sm text-green-600 dark:text-green-400">
                                {formatCurrencyExact(portfolio.returns || "0")}
                              </td>
                              <td className="py-4 px-4 text-right">
                                <Badge variant="outline" className={`font-semibold text-xs uppercase ${portfolio.deploymentStatus === "active" ? "bg-green-500/10 text-green-600 border-green-500/20" : ""}`}>
                                  {portfolio.deploymentStatus || "ACTIVE"}
                                </Badge>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10 text-center">
                        <PieChart className="w-12 h-12 text-muted-foreground/20 mb-3" />
                        <p className="text-muted-foreground font-medium">No holdings found</p>
                        <p className="text-sm text-muted-foreground/50">Your investment allocation details will appear here once processed.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* My Monthly Returns Breakdown */}
                <Card className="bg-card mt-6">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Monthly Returns Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!loadingReturns && returnsData && returnsData.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Month</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Returns (%)</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Returns (Amount)</th>
                              <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {returnsData.map((ret: any) => (
                              <tr key={ret.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                                <td className="py-4 px-4 font-medium text-sm">
                                  {ret.month} {ret.year}
                                </td>
                                <td className="py-4 px-4 text-right text-sm">
                                  <span className="text-green-600 dark:text-green-400 font-medium">
                                    +{ret.returnsPercentage}%
                                  </span>
                                </td>
                                <td className="py-4 px-4 text-right font-medium text-sm text-green-600 dark:text-green-400">
                                  {formatCurrencyExact(ret.returnsAmount)}
                                </td>
                                <td className="py-4 px-4 text-right">
                                  {ret.isWithdrawn ? (
                                    <Badge variant="destructive" className="font-normal text-xs uppercase bg-destructive/10 text-destructive border-none">
                                      Withdrawn
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="font-normal text-xs uppercase text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30">
                                      Re-invested
                                    </Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10 text-center">
                        <TrendingUp className="w-12 h-12 text-muted-foreground/20 mb-3" />
                        <p className="text-muted-foreground font-medium">No returns recorded yet</p>
                        <p className="text-sm text-muted-foreground/50">Your monthly performance will appear here once processed.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                {allocationData.length > 0 ? (
                  <AllocationChart data={allocationData} />
                ) : (
                  <Card className="bg-card h-64 flex items-center justify-center">
                    <p className="text-muted-foreground text-sm">No allocation data available</p>
                  </Card>
                )}

                {portfolio && (() => {
                  const total = parseFloat(portfolio.totalInvested);
                  const cash = parseFloat(portfolio.cashEquivalents || "0");
                  const deployed = total - cash;
                  const deploymentPercentage = total > 0 ? (deployed / total) * 100 : 0;

                  let status: "deployed" | "partial" | "pending" = "pending";
                  if (deploymentPercentage >= 95) status = "deployed";
                  else if (deploymentPercentage > 0) status = "partial";

                  return (
                    <DeploymentStatus
                      status={status}
                      deployed={deployed}
                      total={total}
                    />
                  );
                })()}
              </div>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
